import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Text,
  VStack,
  HStack,
  Flex,
  Spinner,
  Badge,
  Icon,
  IconButton,
  Button,
  useToast,
} from "@chakra-ui/react";
import {
  MdAdd,
  MdLock,
  MdLockOpen,
  MdPeople,
  MdCalendarToday,
} from "react-icons/md";
import axiosInstance from "../routes/axiosInstance";
import { useAuth } from "../context/AuthContext";
import BackButton from "../components/BackButton";

const SpaceListPage = () => {
  const navigate = useNavigate();
  const { currentUser, checkUserAuth } = useAuth();
  const toast = useToast();
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSpaces = async () => {
      if (!currentUser?.token) {
        setLoading(false);
        return;
      }

      const config = {
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${currentUser.token}`,
        },
      };
      setLoading(true);
      setError(null);
      try {
        const res = await axiosInstance.get("/api/spaces", config);
        setSpaces(res.data.data || []);
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch spaces";
        setError(errorMessage);
        toast({
          title: "Error",
          description: errorMessage,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        checkUserAuth(error.response?.status);
      } finally {
        setLoading(false);
      }
    };
    fetchSpaces();
  }, [currentUser?.token, checkUserAuth, toast]);

  // Show loading if no user is available
  if (!currentUser) {
    return (
      <Box bg="gray.50" minH="100vh">
        <Flex direction="column" h="100vh" maxW="md" mx="auto">
          <Flex align="center" justify="center" flex="1">
            <VStack spacing={3}>
              <Spinner size="lg" color="teal.500" />
              <Text fontSize="sm" color="gray.500">
                Loading...
              </Text>
            </VStack>
          </Flex>
        </Flex>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box bg="gray.50" minH="100vh">
        <Flex direction="column" h="100vh" maxW="md" mx="auto">
          <Flex align="center" justify="center" flex="1">
            <VStack spacing={3}>
              <Spinner size="lg" color="teal.500" />
              <Text fontSize="sm" color="gray.500">
                Loading spaces...
              </Text>
            </VStack>
          </Flex>
        </Flex>
      </Box>
    );
  }

  if (error) {
    return (
      <Box bg="gray.50" minH="100vh">
        <Flex direction="column" h="100vh" maxW="md" mx="auto">
          <Flex align="center" justify="center" flex="1">
            <VStack spacing={3} textAlign="center" p={4}>
              <Text fontSize="lg" color="red.500" fontWeight="600">
                Error Loading Spaces
              </Text>
              <Text fontSize="sm" color="gray.500">
                {error}
              </Text>
            </VStack>
          </Flex>
        </Flex>
      </Box>
    );
  }

  return (
    <Box bg="gray.50" minH="100vh">
      <Flex direction="column" h="100vh" maxW="md" mx="auto">
        {/* Header */}
        <Card
          bg="white"
          borderRadius="none"
          borderBottom="1px solid"
          borderColor="gray.200"
          p={3}
          boxShadow="sm"
        >
          <Flex align="center" justify="space-between">
            <Flex align="center">
              <BackButton />
              <Text fontSize="lg" fontWeight="600" color="gray.800" ml={3}>
                My Spaces
              </Text>
            </Flex>
            <IconButton
              aria-label="New Space"
              icon={<MdAdd />}
              colorScheme="teal"
              variant="outline"
              size="md"
              onClick={() => navigate("/spaces/new")}
              borderRadius="lg"
            />
          </Flex>
        </Card>

        {/* Content */}
        <Box
          flex="1"
          bg="white"
          borderLeft="1px solid"
          borderRight="1px solid"
          borderColor="gray.200"
          overflowY="auto"
        >
          {spaces.length === 0 ? (
            <Flex align="center" justify="center" h="full">
              <VStack spacing={4} textAlign="center" p={4}>
                <Text fontSize="lg" color="gray.500" fontWeight="500">
                  No spaces found
                </Text>
                <Text fontSize="sm" color="gray.400">
                  Create your first space to get started
                </Text>
                <Button
                  leftIcon={<MdAdd />}
                  colorScheme="teal"
                  variant="outline"
                  size="md"
                  onClick={() => navigate("/spaces/new")}
                  borderRadius="lg"
                  mt={2}
                >
                  Create Space
                </Button>
              </VStack>
            </Flex>
          ) : (
            <VStack align="stretch" spacing={0} p={4}>
              {spaces.map((space, index) => (
                <Box
                  key={space._id}
                  borderBottom={
                    index < spaces.length - 1 ? "1px solid" : "none"
                  }
                  borderColor="gray.100"
                  pb={index < spaces.length - 1 ? 3 : 0}
                  mb={index < spaces.length - 1 ? 3 : 0}
                >
                  <SpaceCard space={space} navigate={navigate} />
                </Box>
              ))}
            </VStack>
          )}
        </Box>
      </Flex>
    </Box>
  );
};

// Separate component for space card
const SpaceCard = ({ space, navigate }) => {
  if (!space) return null;

  const formatJoinedDate = (dateString) => {
    if (!dateString) return "Unknown";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Unknown";

      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      const todayMidnight = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
      );
      const yesterdayMidnight = new Date(
        yesterday.getFullYear(),
        yesterday.getMonth(),
        yesterday.getDate(),
      );
      const dateMidnight = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
      );

      const timeString = date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });

      if (dateMidnight.getTime() === todayMidnight.getTime()) {
        return `Today at ${timeString}`;
      } else if (dateMidnight.getTime() === yesterdayMidnight.getTime()) {
        return `Yesterday at ${timeString}`;
      } else {
        return `${date.toLocaleDateString()} at ${timeString}`;
      }
    } catch (error) {
      return "Unknown";
    }
  };

  const handleCardClick = () => {
    if (space._id) {
      navigate(`/space/${space._id}/edit`);
    }
  };

  return (
    <Card
      p={4}
      borderRadius="lg"
      border="1px"
      borderColor={space.isActive === false ? "red.200" : "gray.200"}
      bg={space.isActive === false ? "red.50" : "white"}
      _hover={{
        borderColor: space.isActive === false ? "red.300" : "gray.300",
        transform: "translateY(-1px)",
      }}
      transition="all 0.2s"
      cursor="pointer"
      onClick={handleCardClick}
    >
      <VStack align="stretch" spacing={3}>
        {/* Header */}
        <HStack justify="space-between" align="center">
          <HStack align="center" flex="1">
            <Text fontSize="md" fontWeight="600" noOfLines={1}>
              {space.title || "Untitled Space"}
            </Text>
          </HStack>
          <Badge colorScheme="teal" borderRadius="full" fontSize="xs">
            {space.kind || "Unknown"}
          </Badge>
        </HStack>

        {/* Details */}
        <HStack justify="space-between" color="gray.600" fontSize="sm">
          <HStack>
            <Icon as={MdPeople} />
            <Text>
              {space.memberCount || 0} member
              {(space.memberCount || 0) !== 1 ? "s" : ""}
            </Text>
          </HStack>

          <HStack>
            {space.participantMode === "AUTHENTICATED" ? (
              <>
                <Icon as={MdLock} color="teal.500" />
                <Text color="teal.600" fontSize="xs">
                  Auth Required
                </Text>
              </>
            ) : (
              <>
                <Icon as={MdLockOpen} color="orange.500" />
                <Text color="orange.600" fontSize="xs">
                  Anonymous
                </Text>
              </>
            )}
          </HStack>
        </HStack>

        {/* Created date */}
        <HStack color="gray.500" fontSize="xs">
          <Icon as={MdCalendarToday} />
          <Text>Created {formatJoinedDate(space.createdAt)}</Text>
        </HStack>
      </VStack>
    </Card>
  );
};

export default SpaceListPage;
